import React, { useEffect, useState } from "react";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";

function AuditRetention({ item, lang, onUpdated }) {
  const [reason, setReason] = useState(item.retention?.reason || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const text = (it, en) => lang === "it" ? it : en;
  if (!item.retention?.eligible_type) return <span>{text("Regola separata per privacy e moderazione", "Separate privacy and moderation policy")}</span>;
  const save = async (mode) => {
    setBusy(true); setError("");
    try {
      await api.patch(`/admin/audit-log/${item.id}/retention`, { mode, reason,
        expected_reviewed_at: null, expected_retention_changed_at: item.retention?.changed_at || null });
      await onUpdated();
    } catch (e) { setError(extractError(e, text("Ricarica e riprova", "Reload and try again"))); }
    finally { setBusy(false); }
  };
  return <div className="space-y-2 min-w-[220px]">
    <p>{item.retention.hold ? text("Cancellazione sospesa", "Deletion on hold") : item.retention.expires_at
      ? text("Scadenza: ", "Due: ") + new Date(item.retention.expires_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")
      : text("Da classificare", "Needs review")}</p>
    <p>{text("Conferma solo se non serve per controversie o altri obblighi. Rimozione dopo 6 mesi dall’evento, anche subito se già trascorsi.", "Confirm only if not needed for disputes or other obligations. Removal after 6 months from the event, including immediately if already overdue.")}</p>
    <button disabled={busy} onClick={() => save("close")} className="nvl-btn nvl-btn-secondary">{text("Conferma registro ordinario", "Confirm ordinary record")}</button>
    <label className="block">{text("Motivo della sospensione", "Reason for hold")}<input className="nvl-input" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
    <button disabled={busy || !reason.trim()} onClick={() => save("hold")} className="nvl-btn nvl-btn-secondary">{text("Sospendi la cancellazione", "Hold deletion")}</button>
    {item.retention.review_at && <p>{text("Riesame manuale entro: ", "Manual review by: ") + new Date(item.retention.review_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</p>}
    {error && <p role="alert">{error}</p>}
  </div>;
}

export default function AdminAuditLog() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/audit-log");
        setItems(r.data.items || []);
      } catch (e) {
        setError(extractError(e, "Error"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const reload = async () => {
    const r = await api.get("/admin/audit-log");
    setItems(r.data.items || []);
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang === "it" ? "it-IT" : "en-GB");
    } catch { return iso; }
  };

  return (
    <div data-testid="admin-audit-log-page">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {t("app.admin.nav.auditLog")}
      </p>
      <h1 className="mt-2 text-3xl md:text-4xl">{t("app.admin.audit.title")}</h1>
      <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {t("app.admin.audit.subtitle")}
      </p>

      <div className="mt-6 nvl-card overflow-x-auto">
        {loading ? (
          <div className="p-6 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("common.loading")}
          </div>
        ) : error ? (
          <div className="p-6 text-sm" style={{ color: "hsl(var(--destructive))" }}>
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm" data-testid="admin-audit-log-empty">
            {t("app.admin.audit.empty")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                <th className="p-3">{t("app.admin.audit.columns.when")}</th>
                <th className="p-3">{t("app.admin.audit.columns.admin")}</th>
                <th className="p-3">{t("app.admin.audit.columns.action")}</th>
                <th className="p-3">{t("app.admin.audit.columns.target")}</th>
                <th className="p-3">{t("app.admin.audit.columns.details")}</th>
                <th className="p-3">{lang === "it" ? "Conservazione" : "Retention"}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t"
                    style={{ borderColor: "hsl(var(--nvl-border))" }}
                    data-testid={`audit-row-${it.id}`}>
                  <td className="p-3 text-xs">{fmt(it.created_at)}</td>
                  <td className="p-3 text-xs">{it.actor_user_id || "system"}</td>
                  <td className="p-3">{it.action}</td>
                  <td className="p-3 text-xs">
                    {it.target_user_id ? `u:${it.target_user_id}` : ""}
                    {it.target_report_id ? ` r:${it.target_report_id}` : ""}
                  </td>
                  <td className="p-3 text-xs">
                    <pre className="whitespace-pre-wrap break-all max-w-md">
                      {JSON.stringify(it.details, null, 0)}
                    </pre>
                  </td>
                  <td className="p-3 text-xs"><AuditRetention item={it} lang={lang} onUpdated={reload} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
